/**
 * Interactive Particle Network - Premium background effect
 * Creates an animated network of interconnected particles
 */

class ParticleNetwork {
  constructor(canvasId) {
    this.canvas = document.getElementById(canvasId);
    if (!this.canvas) return;
    
    this.ctx = this.canvas.getContext('2d');
    this.particles = [];
    this.mouse = { x: null, y: null, radius: 150 };
    this.colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#a855f7'];
    
    // Performance settings
    this.isMobile = window.innerWidth < 768;
    this.particleCount = this.isMobile ? 40 : 80;
    this.connectionDistance = this.isMobile ? 100 : 150;
    this.targetFPS = this.isMobile ? 30 : 45;
    this.lastRender = 0;
    this.isPaused = false;
    
    this.init();
  }

  init() {
    this.resize();
    this.createParticles();
    this.setupEventListeners();
    this.animate();
  }

  resize() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }

  createParticles() {
    this.particles = [];
    
    for (let i = 0; i < this.particleCount; i++) {
      this.particles.push({
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1,
        color: this.colors[Math.floor(Math.random() * this.colors.length)],
        alpha: Math.random() * 0.5 + 0.2
      });
    }
  }

  setupEventListeners() {
    window.addEventListener('resize', () => {
      this.resize();
      this.createParticles();
    }, { passive: true });
    
    const onMouseMove = (e) => {
      this.mouse.x = e.clientX;
      this.mouse.y = e.clientY;
    };

    const trackedMouseMove = typeof window.rafThrottle === 'function'
      ? window.rafThrottle(onMouseMove)
      : onMouseMove;

    window.addEventListener('mousemove', trackedMouseMove, { passive: true });
    
    window.addEventListener('mouseout', () => {
      this.mouse.x = null;
      this.mouse.y = null;
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
      this.isPaused = document.hidden;
    });
  }

  animate(timestamp = 0) {
    requestAnimationFrame(() => this.animate());

    if (this.isPaused) return;

    const frameInterval = 1000 / this.targetFPS;
    if (timestamp - this.lastRender < frameInterval) return;
    this.lastRender = timestamp;
    
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Update and draw particles
    this.particles.forEach((particle, i) => {
      // Update position
      particle.x += particle.vx;
      particle.y += particle.vy;
      
      // Bounce off edges
      if (particle.x < 0 || particle.x > this.canvas.width) particle.vx *= -1;
      if (particle.y < 0 || particle.y > this.canvas.height) particle.vy *= -1;
      
      // Mouse interaction
      if (this.mouse.x !== null && this.mouse.y !== null) {
        const dx = this.mouse.x - particle.x;
        const dy = this.mouse.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < this.mouse.radius) {
          const force = (this.mouse.radius - distance) / this.mouse.radius;
          particle.x -= dx * force * 0.02;
          particle.y -= dy * force * 0.02;
        }
      }
      
      // Draw particle
      this.ctx.beginPath();
      this.ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
      this.ctx.fillStyle = particle.color;
      this.ctx.globalAlpha = particle.alpha;
      this.ctx.fill();
      
      // Draw connections
      for (let j = i + 1; j < this.particles.length; j++) {
        const other = this.particles[j];
        const dx = particle.x - other.x;
        const dy = particle.y - other.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < this.connectionDistance) {
          this.ctx.beginPath();
          this.ctx.moveTo(particle.x, particle.y);
          this.ctx.lineTo(other.x, other.y);
          this.ctx.strokeStyle = particle.color;
          this.ctx.globalAlpha = (1 - distance / this.connectionDistance) * 0.2;
          this.ctx.lineWidth = 0.5;
          this.ctx.stroke();
        }
      }
    });
    
    this.ctx.globalAlpha = 1;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Create canvas element
  const existingParticles = document.getElementById('particles-js');
  if (existingParticles) {
    const canvas = document.createElement('canvas');
    canvas.id = 'particle-network';
    canvas.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 1;
    `;
    existingParticles.parentNode.insertBefore(canvas, existingParticles);
    existingParticles.style.display = 'none'; // Hide old particles
    
    // Initialize after page load
    setTimeout(() => {
      window.particleNetwork = new ParticleNetwork('particle-network');
    }, 2000);
  }
});
